import React, {useEffect, useState} from 'react';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import * as yup from "yup";
import {getLanguage, t} from "react-switch-lang";
import {Row, Col, Spinner} from 'react-bootstrap';
import {yupResolver} from "@hookform/resolvers";
import SelectInfinite from '../../../../../components/FormFields/SelectInfinite';
import InputText from '../../../../../components/FormFields/InputText';
import InputDate from '../../../../../components/FormFields/inputDate/InputDate';
import TextArea from '../../../../../components/FormFields/Textarea';
import {defaultMinValueZero} from '../../../../../utils/const';
import {
  formatNumberForDisplay,
  formatNumberInput,
  handlePasteAndValidate,
  maxDecimalNumber,
  onDecimalNumberInputChange, parseFormattedNumber, setFormattedDefault,
  testIsGreaterThanMin
} from '../../../../../utils/helpers';
import {format, parseISO} from 'date-fns';
import {Toast} from '../../../../../utils/sweetAlert';
import {
  createFinanceForm,
  editFinanceForm,
  getFinanceForm, getFinanceOverallRiskAssessmentLevels,
} from '../../../../../services/externalizations/sectorFinanceService';
import {useParams} from 'react-router-dom';
import {
  customStyles,
  handleCancelExternalization,
  isUserReviewer, statusesOfVerification,
} from '../../../../../utils/externalizationFormOptions';
import Swal from 'sweetalert2';
import {useExternalizationContext} from '../../../../../contexts/ExternalizationContext';
import {useUserContext} from '../../../../../contexts/UserContext';
import VerifyExternalizationModal from '../../../components/verifyExternalizationModal/VerifyExternalizationModal';
import FormActionButtons from '../../../components/formActionButtons/FormActionButtons';

const SectorFinance = ({cycleId, isLastCycle, isEditAllowedByStatus, isVerified, externalizationFormConfigData}) => {
  const [disabled, setDisabled] = useState(false);
  const [sectorFinanceFormId, setSectorFinanceFormId] = useState();
  const [loading,setLoading] = useState(false);
  const [isReview,setIsReview] = useState(false);
  const [populated,setPopulated] = useState(false);
  const [showEditBtn,setShowEditBtn] = useState(false);
  const [defaultData,setDefaultData] = useState([]);
  const [impactOnBankOperatingCosts, setImpactOnBankOperatingCosts] = useState('')
  const [impactOnBankOperatingPerformance, setImpactOnBankOperatingPerformance] = useState('')
  const [totalRiskAssessment, setTotalRiskAssessment] = useState([])
  const [riskDescription, setRiskDescription] = useState('')
  const [edit,setEdit] = useState(showEditBtn);
  const externalizationConfigData = useExternalizationContext();
  const user = useUserContext()
  const [responsibleUserId, setResponsibleUserId] = useState()
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verificationStatus, setVerificationStatus]= useState('')
  const canVerify = verificationStatus === 'PENDING'
  const userCanVerifyForm = user?.id === responsibleUserId
  const toggleVerifyModal = () => setVerifyModalOpen(prev => !prev);

  const schema = yup.object().shape({
    processingDate: yup.date()
      .nullable()
      .typeError(t('message.fieldRequired'))
      .required(t('message.fieldRequired')),
    serviceCost: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
     .test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    amortization: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
     .test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    totalBankOperatingCosts: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
     .test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    netInterestAndFeeIncome: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .test('is-greater-than-min', t('message.minValue', {min: defaultMinValueZero}), (value) => {
        return testIsGreaterThanMin(value,defaultMinValueZero);
      })
     .test('max-two-decimals', t('message.maxTwoDecimals'), (value) => {
        return maxDecimalNumber(value);
      })
      .typeError(t('message.onlyDecimalNumbers')),
    comment: yup
      .string()
      .trim()
      .max(2000, t('message.max', {max: 2000}))
  });
  const methods = useForm({
    resolver: yupResolver(schema),
  });
  const {handleSubmit, setValue, trigger, watch, reset} = methods;
  const watchCostOfService = watch("serviceCost")
  const watchDepreciation = watch("amortization")
  const watchTotalCost = watch("totalCosts")
  const watchTotalOperatingExpensesOfBank = watch("totalBankOperatingCosts")
  const watchNetInterestAndFees = watch("netInterestAndFeeIncome")
  const watchPercentageShareOperatingCosts = watch("operatingCostsPercentage")
  const watchPercentageDecreaseNetInterestAndFees = watch("netIncomeReductionPercentage")
  const watchFinanceOverallRiskAssessmentLevel = watch("financeOverallRiskAssessmentLevel");
  const lang = getLanguage();
  const { id } = useParams();
  const submitForm = async (data) => {
    const formatProcessingDate =
      format(new Date(data?.processingDate), 'yyyy-MM-dd');
    setDisabled(true);
    const dataToSubmit = {
      ...(edit && {
        id: defaultData?.id ? defaultData?.id : sectorFinanceFormId,
      }),
      processingDate: formatProcessingDate,
      amortization: parseFormattedNumber(data?.amortization),
      serviceCost: parseFormattedNumber(data?.serviceCost),
      totalCosts: parseFormattedNumber(data?.totalCosts),
      totalBankOperatingCosts: parseFormattedNumber(data?.totalBankOperatingCosts),
      operatingCostsPercentage: parseFormattedNumber(data?.operatingCostsPercentage),
      netInterestAndFeeIncome: parseFormattedNumber(data?.netInterestAndFeeIncome),
      netIncomeReductionPercentage: parseFormattedNumber(data?.netIncomeReductionPercentage),
      operatingCostsImpactLevel: impactOnBankOperatingCosts?.value,
      businessResultImpactLevel: impactOnBankOperatingPerformance?.value,
      financeOverallRiskAssessmentLevel: { id: data?.financeOverallRiskAssessmentLevel?.value },
      comment: data?.comment,
      populated: true,
      cycle: {id: parseInt(cycleId)},
      formConfig: externalizationFormConfigData,
      userInCharge: {id: externalizationFormConfigData?.defaultDepartment?.userInChargeForExternalizationId},
      responsibleUser: {id: externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId}
    };
    if(edit) {
      try{
        const res = await editFinanceForm(defaultData?.id ? defaultData?.id : sectorFinanceFormId,dataToSubmit)
        if (res?.status === 200) {
          Toast.fire({
            title: t('message.updateSuccess2', {
              entity: t('externalization.sectorFinance.sectorFinance'),
            }),
            icon: 'success',
          });
          const newVerificationStatus = res?.data?.verificationStatus;
          if (newVerificationStatus && newVerificationStatus !== verificationStatus) {
            setTimeout(() => {
              window.location.reload();
            }, 800);
          } else {
            setVerificationStatus(newVerificationStatus);
            setDisabled(true);
          }
        }
        setDisabled(true);
      }
      catch (err){
        if (err?.response?.data?.code === 'responsible-user-for-externalization-is-missing') {
          Swal.fire({
            text:
              lang === 'eng'
                ? err.response.data.description
                : t('message.responsibleUserForExternalizationMissing'),
            icon: 'error',
          });
          setDisabled(false)
          setEdit(false)
        } else {
          Toast.fire({
            title: t('message.errorApi'),
            icon: 'error',
          });
        }
      }
    }else {
      try{
        const response = await createFinanceForm(dataToSubmit)
        if (response?.status === 201) {
          Toast.fire({
            title: t('message.addSuccess2', {
              entity: t('externalization.sectorFinance.sectorFinance'),
            }),
            icon: 'success',
          });
          setDisabled(true)
          setEdit(true)
          setIsReview(true);
          setVerificationStatus('PENDING')
          setSectorFinanceFormId(response?.data?.id)
          setResponsibleUserId(externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId);
        }
      } catch (err){
        if (err?.response?.data?.code === 'responsible-user-for-externalization-is-missing') {
          Swal.fire({
            text:
              lang === 'eng'
                ? err.response.data.description
                : t('message.responsibleUserForExternalizationMissing'),
            icon: 'error',
          });
          setDisabled(false)
          setEdit(false)
        } else {
          Toast.fire({
            title: t('message.errorApi'),
            icon: 'error',
          });
        }
      }
    }
  }
  useEffect(() => {
    async function fetchRiskLevels() {
        const {data} = await getFinanceOverallRiskAssessmentLevels();
      const validLevels = data.filter(r => r.value >= 0 && r.value <= 2);
        setTotalRiskAssessment(validLevels);
    }
    fetchRiskLevels();
  }, []);

  function determineRiskValue(opLevel, busLevel) {
    const impactMap = {
      "LOW": 0,
      "MEDIUM": 1,
      "HIGH": 2
    };
    const op = impactMap[opLevel];
    const bus = impactMap[busLevel];
    if ((op === 0 && bus === 0) || (op === 0 && bus === 1)) {
      return 0;
    }
    if ((op === 1 && bus === 1) || (op === 1 && bus === 2) || (op=== 1 && bus === 0) || (op === 0 && bus === 2)) {
      return 1;
    }
    return 2;
  }

  function getRiskAssessmentDTO(opLevel, busLevel, riskLevelList) {
    const riskValue = determineRiskValue(opLevel, busLevel);
    return riskLevelList.find(r => r.value === riskValue);
  }

  useEffect(() => {
    if (impactOnBankOperatingCosts && impactOnBankOperatingPerformance && totalRiskAssessment.length > 0) {
      const dto = getRiskAssessmentDTO(impactOnBankOperatingCosts?.value, impactOnBankOperatingPerformance?.value, totalRiskAssessment);
     if(dto) {
       const transformed = {
         value: dto.id,
         label: lang === 'mne' ? dto.name : dto.nameEn
       };
       setValue('financeOverallRiskAssessmentLevel', transformed);
     }
    }
  }, [impactOnBankOperatingCosts?.value, impactOnBankOperatingPerformance?.value, totalRiskAssessment]);
  useEffect(() => {
      getFinanceForm(cycleId).then((res) => {
          const form = res?.data?.[0] ?? null;
        if(form) {
          setDefaultData(form);
          setVerificationStatus(form?.verificationStatus)
          setPopulated(!!form?.populated);
          setEdit(!!form?.populated);
          setDisabled(!!form?.populated);
          setIsReview(!!form?.populated && isUserReviewer(externalizationConfigData,user,'financeForm'));
          setShowEditBtn(!form?.populated === false || !isUserReviewer(externalizationConfigData,user,'financeForm'));
          setResponsibleUserId(externalizationFormConfigData?.defaultDepartment?.userResponsibleForExternalizationId);
        }
        if(res.data[0]?.populated) {
          setPopulated(false);
          setEdit(true);
        }else {
          setValue('processingDate', new Date());
          setEdit(false)
          setDisabled(!isUserReviewer(externalizationConfigData,user,'financeForm'));
        }
      })
  }, [id]);


  const setTotalCostValue = (num) => {
    if(num) {
      setValue(
        'totalCosts',
        formatNumberInput(
          formatNumberForDisplay(num).includes(',')
            ? formatNumberForDisplay(num)
            : formatNumberForDisplay(num) + ',00'
          , 2
        )
      );
    } else {
      setValue('totalCosts',0)
    }
  }
  useEffect(() => {
    if (watchCostOfService && watchDepreciation) {
      let no =
        parseFormattedNumber(watchCostOfService) +
        parseFormattedNumber(watchDepreciation);
      setTotalCostValue(no)
    }
  }, [watchCostOfService, watchDepreciation]);


  const setPercentageShareOperatingCosts = (num) => {
    if(num) {
      setValue(
        'operatingCostsPercentage',
        formatNumberInput(
          formatNumberForDisplay(num).includes(',')
            ? formatNumberForDisplay(num)
            : formatNumberForDisplay(num) + ',00'
          , 2
        )
      );
    } else {
      setValue('operatingCostsPercentage',0)
    }
  }
  useEffect(() => {
    if (watchTotalCost && watchTotalOperatingExpensesOfBank) {
      let no =
        (parseFormattedNumber(watchTotalCost) /
          parseFormattedNumber(watchTotalOperatingExpensesOfBank))*100 ;
      setPercentageShareOperatingCosts(no)
    }

  }, [watchTotalCost, watchTotalOperatingExpensesOfBank]);

  const setPercentageDecreaseNetInterestAndFee = (num) => {
    if(num) {
      setValue(
        'netIncomeReductionPercentage',
        formatNumberInput(
          formatNumberForDisplay(num).includes(',')
            ? formatNumberForDisplay(num)
            : formatNumberForDisplay(num) + ',00'
          , 2
        )
      );
    } else {
      setValue('netIncomeReductionPercentage',0)
    }
  }
  useEffect(() => {
    if (watchTotalCost && watchNetInterestAndFees) {
      let no =
        (parseFormattedNumber(watchTotalCost) /
          (parseFormattedNumber(watchNetInterestAndFees) + parseFormattedNumber(watchTotalCost)))*100 ;
      setPercentageDecreaseNetInterestAndFee(no)
    }
  }, [watchTotalCost, watchNetInterestAndFees]);
  useEffect(() => {
    if (watchPercentageShareOperatingCosts) {
      const percentage = parseFormattedNumber(watchPercentageShareOperatingCosts) / 100;
      if (percentage <= 0.03) {
        setImpactOnBankOperatingCosts({ value: 'LOW', label: t('externalization.lowLevel') });
      } else if (percentage > 0.03 && percentage <= 0.05) {
        setImpactOnBankOperatingCosts({ value: 'MEDIUM', label: t('externalization.mediumLevel') });
      } else {
        setImpactOnBankOperatingCosts({ value: 'HIGH', label: t('externalization.highLevel') });
      }
    }
  }, [watchPercentageShareOperatingCosts, watchTotalCost, watchTotalOperatingExpensesOfBank]);
  useEffect(() => {
    if (watchPercentageDecreaseNetInterestAndFees) {
      const percentage = parseFormattedNumber(watchPercentageDecreaseNetInterestAndFees) / 100;
      if (percentage <= 0.10) {
        setImpactOnBankOperatingPerformance({ value: 'LOW', label: t('externalization.lowLevel') });
      } else if (percentage > 0.10 && percentage <= 0.20) {
        setImpactOnBankOperatingPerformance({ value: 'MEDIUM', label: t('externalization.mediumLevel') });
      } else {
        setImpactOnBankOperatingPerformance({ value: 'HIGH', label: t('externalization.highLevel') });
      }
    }
  }, [watchPercentageDecreaseNetInterestAndFees]);
  useEffect(() => {
    if (watchFinanceOverallRiskAssessmentLevel && totalRiskAssessment.length > 0) {
      const matchedRisk = totalRiskAssessment.find(
        (item) => item.id === watchFinanceOverallRiskAssessmentLevel.value
      );
      if (matchedRisk) {
        setRiskDescription({
          value: matchedRisk.value,
          label: lang === 'mne' ? matchedRisk.description : matchedRisk.descriptionEn,
        });
      }
    }
  }, [watchFinanceOverallRiskAssessmentLevel, totalRiskAssessment]);



  useEffect(() => {
    if (
      defaultData?.financeOverallRiskAssessmentLevel &&
      totalRiskAssessment.length > 0
    ) {
      setValue("financeOverallRiskAssessmentLevel", {
        value: defaultData.financeOverallRiskAssessmentLevel.id,
        label:
          lang === "mne"
            ? defaultData.financeOverallRiskAssessmentLevel.name
            : defaultData.financeOverallRiskAssessmentLevel.nameEn
      });
    }
    if(defaultData?.processingDate) {
      setValue('processingDate',parseISO(defaultData?.processingDate))
    }
    if (defaultData?.amortization) {
      setFormattedDefault("amortization", defaultData.amortization,setValue);
    }
    if (defaultData?.totalBankOperatingCosts) {
      setFormattedDefault("totalBankOperatingCosts", defaultData.totalBankOperatingCosts,setValue);
    }
    if (defaultData?.serviceCost) {
      setFormattedDefault("serviceCost", defaultData.serviceCost,setValue);
    }
    if (defaultData?.netInterestAndFeeIncome) {
      setFormattedDefault("netInterestAndFeeIncome", defaultData.netInterestAndFeeIncome,setValue);
    }
  }, [defaultData]);

  if (loading) {
    return (
        <div className="text-center mt-4 mb-4">
          <Spinner animation="border" role="status" />
        </div>
    );
  }
  return (
    <>
      <FormActionButtons
        isReview={isReview}
        edit={edit}
        disabled={disabled}
        isLastCycle={isLastCycle}
        isEditAllowedByStatus={isEditAllowedByStatus}
        userCanVerifyForm={userCanVerifyForm}
        canVerify={canVerify}
        toggleVerifyModal={toggleVerifyModal}
        setDisabled={setDisabled}
      />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mb-2">
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.departments')}
                </label>
                <SelectInfinite
                  api="departments"
                  defaultValue={
                    externalizationFormConfigData?.defaultDepartment
                      ? {
                        value: externalizationFormConfigData?.defaultDepartment?.id,
                        label:
                          lang === 'mne'
                            ? externalizationFormConfigData?.defaultDepartment?.name
                            : externalizationFormConfigData?.defaultDepartment?.nameEn,
                      }
                      : null
                  }
                  name="departmentInCharge"
                  labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                  className="select"
                  isDisabled={true}
                />
              </Col>
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.date')}
                </label>
                <InputDate
                  name="processingDate"
                  className={'date-input'}
                  defaultValue={defaultData?.processingDate !== undefined ? format(new Date(defaultData?.processingDate), 'yyyy-MM-dd') : new Date()}
                  disabled={disabled}
                />
              </Col>
            </Row>
            <br/>
            <label className="font-weight-bold mb-4">
              {t('externalization.sectorFinance.calculationCostsAndFinancialResult')}
            </label>
            <Row className="mb-2">
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.costOfService')}
                </label>
                <InputText
                  type="text"
                  name="serviceCost"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.serviceCost}
                  placeholder={t('externalization.sectorFinance.placeholder.costOfService')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "serviceCost")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'serviceCost')}
                />
              </Col>
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.depreciation')}
                </label>
                <InputText
                  type="text"
                  name="amortization"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.amortization}
                  placeholder={t('externalization.sectorFinance.placeholder.depreciation')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "depreciation")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'amortization')}
                />
              </Col>
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.totalCost')}
                </label>
                <InputText
                  type="text"
                  name="totalCosts"
                  defaultValue={defaultData?.totalCosts}
                  className="form-control input-field"
                  disabled
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "totalCosts")}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.totalOperatingExpensesOfBank')}
                </label>
                <InputText
                  type="text"
                  name="totalBankOperatingCosts"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.totalBankOperatingCosts}
                  placeholder={t('externalization.sectorFinance.placeholder.totalOperatingExpensesOfBank')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "totalBankOperatingCosts")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'totalBankOperatingCosts')}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <br/>
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.percentageShareOperatingCosts')}
              </label>
                <InputText
                  disabled
                  type="text"
                  name="operatingCostsPercentage"
                  defaultValue={defaultData?.operatingCostsPercentage}
                  className="form-control input-field"
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "operatingCostsPercentage")}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <br/>
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.netInterestAndFees')}
                </label>
                <InputText
                  type="text"
                  name="netInterestAndFeeIncome"
                  className="form-control input-field"
                  disabled={disabled}
                  defaultValue={defaultData?.netInterestAndFeeIncome}
                  placeholder={t('externalization.sectorFinance.placeholder.netInterestAndFees')}
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "netInterestAndFeeIncome")}
                  onPaste={(e) => handlePasteAndValidate(e, setValue, trigger, 'netInterestAndFeeIncome')}
                />
              </Col>
            </Row>
            <Row className="mb-2">
            <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.percentageDecreaseNetInterestAndFee')}
                </label>
                <InputText
                  disabled
                  type="text"
                  name="netIncomeReductionPercentage"
                  defaultValue={defaultData?.netIncomeReductionPercentage}
                  className="form-control input-field"
                  onChange={(e) => onDecimalNumberInputChange(e, setValue, "netIncomeReductionPercentage")}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.impactOnBankOperatingCosts')}
                </label>
                <InputText
                  name={impactOnBankOperatingCosts}
                  defaultValue={defaultData?.netIncomeReductionPercentage}
                  className="form-control input-field"
                  value={impactOnBankOperatingCosts?.label}
                  type="text"
                  disabled={true}
                />
              </Col>
            <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.impactOnBankOperatingPerformance')}
                </label>
                <InputText
                  name={impactOnBankOperatingPerformance}
                  className="form-control input-field"
                  value={impactOnBankOperatingPerformance?.label}
                  type="text"
                  disabled={true}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.totalRiskAssessment')}
                </label>
                <SelectInfinite
                  options={totalRiskAssessment}
                  name="financeOverallRiskAssessmentLevel"
                  labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                  className="select"
                  placeholder={''}
                  isDisabled={true}
                  styles={customStyles}
                  defaultValue={lang === 'mne' ? defaultData?.financeOverallRiskAssessmentLevel?.name : defaultData?.financeOverallRiskAssessmentLevel?.nameEn}
                />
              </Col>
              <Col className="col-6 col-xl-3">
              <label className="label w-100 mt-2">
                  {t('externalization.sectorFinance.riskDescription')}
                </label>
                <textarea
                  key={riskDescription}
                  value={riskDescription?.label}
                  type="text"
                  defaultValue={lang === 'mne' ? defaultData?.financeOverallRiskAssessmentLevel?.description : defaultData?.financeOverallRiskAssessmentLevel?.descriptionEn}
                  disabled={true}
                  rows={4}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('comments.comment')}
                  <span className="optional register line-height">
                  {t('common.optional')}
                </span>
                </label>
                <TextArea
                  name="comment"
                  className={`form-control text-area `}
                  placeholder={t('placeholder.comment')}
                  disabled={disabled}
                  defaultValue={defaultData?.comment}
                />
              </Col>
            </Row>
            <Row className="mb-2 pb-4">
            <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.analysisDoneBy')}
                </label>
                <InputText
                  type="text"
                  name="userInCharge"
                  className="form-control input-field"
                  disabled={true}
                  id="userInCharge"
                  defaultValue={externalizationFormConfigData?.defaultDepartment.userInChargeForExternalizationFullName}
                />
              </Col>
              <Col className="col-6 col-xl-3">
                {externalizationFormConfigData?.defaultDepartment?.deputyUserInChargeForExternalizationFullName && <>
                  <label className="label w-100 mt-2">{t('departments.userInChargeReplacementForExternalization')}</label>
                  <InputText
                    type="text"
                    name="userInChargeDeputy"
                    className="form-control input-field"
                    disabled={true}
                    id="userInCharge"
                    defaultValue={externalizationFormConfigData?.defaultDepartment?.deputyUserInChargeForExternalizationFullName}
                  /></>}
              </Col>
              <Col className="col-6 col-xl-3">
                <label className="label w-100 mt-2">
                  {t('externalization.verificationDoneBy')}
                </label>
                <InputText
                  type="text"
                  name="responsibleUser"
                  className="form-control input-field"
                  disabled={true}
                  id="responsibleUser"
                  defaultValue={externalizationFormConfigData?.defaultDepartment.userResponsibleForExternalizationFullName}
                />
              </Col>
              <Col className="col-6 col-xl-3">
              {edit && <>  <label className="label w-100 mt-2">{t('externalization.verificationStatus')}</label>
                <InputText
                  name="verificationStatus"
                  type="text"
                  className="form-control input-field"
                  placeholder={t( 'externalization.placeholder.verificationStatus')}
                  disabled={true}
                  defaultValue={statusesOfVerification.find(
                    (status) => status.value === verificationStatus)?.label}
                /></>}
              </Col>
            </Row>
            {(!edit || (edit && !disabled)) && isUserReviewer(externalizationConfigData, user, 'financeForm') && <Row className="mt-5 mb-3 form-footer">
              <button
                className="secondary-btn"
                type="reset"
                onClick={handleCancelExternalization}>
                {t('buttons.cancel')}
              </button>
              <button className="primary-btn" disabled={disabled}>{t('buttons.save')}</button>
            </Row>}
          </form>
        </FormProvider>
      <VerifyExternalizationModal
        open={verifyModalOpen}
        formType={externalizationFormConfigData?.formKey}
        formId={defaultData?.id ? defaultData?.id : sectorFinanceFormId}
        isOpen={setVerifyModalOpen}
      />
    </>
  );
};

export default SectorFinance;